import {
  FormSubmissionsTableFilterArea,
  FormSubmissionsTableFilterAreaDrawerContent,
  FormSubmissionsViewFilterArea,
} from "#src/batteries-included-components/FilterAreas/FormsFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { useExportFormSubmissionAsPDF } from "#src/components/Forms/useExportFormSubmissionAsPDF";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#src/components/Redux/reducers/tableStateReducer";
import { useListFormSubmissions } from "#src/components/hooks/adapters/useFormSubmissions";
import { useGetManyUsers } from "#src/components/hooks/adapters/useUsers";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { linkToFormCategoryDetails } from "#src/routes/forms/categories/[categoryId]";
import { linkToFormTemplateDetail } from "#src/routes/forms/categories/[categoryId]/templates/[formTemplateId]";
import { linkToUpdateFormSubmission } from "#src/routes/forms/categories/[categoryId]/templates/[formTemplateId]/update-form-submission";
import {
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableUserInterfaceConfiguration,
} from "#src/utils/table";
import { useQueries } from "@tanstack/react-query";
import {
  DataTable,
  DataTablePanel,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  ItemAction,
} from "@validereinc/common-components";
import {
  FormSubmissionAdapter,
  FormSubmissionGetListRequestType,
  FormSubmissionStatus,
  FormSubmissionType,
  Resources,
  SortDirection,
  UserReturnType,
  type GroupedByEntryType,
} from "@validereinc/domain";
import { toFlattenedObject } from "@validereinc/utilities";
import React, { useMemo } from "react";

export const DraftFormSubmissionsList = () => {
  const [
    filterConfigStorageKey,
    tableConfigStorageKey,
    viewConfigStorageKey,
    tableStateStorageKey,
    groupByTablesStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "view-configuration",
      "table-state",
      "group-by-tables-state",
    ],
    ["forms", "submissions", "drafts"]
  );
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
    matchingCompanyConfiguration,
    matchingPersonalConfiguration,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);
  const [viewFilters] = useStickyState<FormSubmissionGetListRequestType>(
    {},
    viewConfigStorageKey
  );
  const [tableFilters] = useStickyState<FormSubmissionGetListRequestType>(
    {},
    filterConfigStorageKey
  );
  const filters = useMemo<any>(
    () => ({ ...tableFilters, ...viewFilters }),
    [tableFilters, viewFilters]
  );
  const { created_at, ...restFilters } = filters;
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const draftQueryPayload: Parameters<typeof FormSubmissionAdapter.getList>[0] =
    {
      page: tableState.page,
      pageSize: tableState.pageSize,
      sortBy: tableState.sortBy,
      sortDirection: tableState.sortDirection,
      groupBy: tableState.groupBy,
      filters: {
        created_at,
        ...toFlattenedObject({
          ...restFilters,
          status: FormSubmissionStatus.DRAFT,
          "form_schema.status": "active",
        }),
      },
    };

  const {
    data: draftSubmissions,
    isLoading: isSubmissionsLoading,
    isFetching: isSubmissionsFetching,
  } = useListFormSubmissions({
    ...draftQueryPayload,
    meta: { answers: true },
  });

  const items = draftSubmissions?.data ?? [];
  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...draftQueryPayload,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? draftQueryPayload)
  );

  const formSubmissionsGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: [Resources.FORM_SUBMISSION, "getList", d.payload],
      queryFn: () => FormSubmissionAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const formSubmissionsCreatedByUsers = useGetManyUsers(
    Array.from(
      new Set(
        tableState.groupBy.length
          ? formSubmissionsGroupedByQueries.flatMap(
              (q) =>
                q.data?.data.map(({ created_by }) => created_by ?? "") ?? []
            )
          : draftSubmissions?.data.map(({ created_by }) => created_by ?? "")
      )
    )
  );
  const formSubmissionsCreatedByUsersMap = useMemo(() => {
    return formSubmissionsCreatedByUsers.reduce<Record<string, UserReturnType>>(
      (map, q) => {
        if (!q.data?.id || map[q.data.id]) {
          return map;
        }

        map[q.data.id] = q.data;
        return map;
      },
      {}
    );
  }, [formSubmissionsCreatedByUsers]);
  const exportPDFMutation = useExportFormSubmissionAsPDF({
    includeEmptyAnswers: true,
    showUpdatedAt: true,
    metaUserDataMap: formSubmissionsCreatedByUsersMap,
  });

  const headers: Array<HeaderType<FormSubmissionType>> = useMemo(
    () => [
      {
        label: "Name",
        key: "id",
        renderComponent: ({ item }) => (
          <RoutingLink
            to={linkToUpdateFormSubmission(
              item?.form_schema?.form_category_id,
              item?.form_schema_id,
              item?.id
            )}
          >{`${item.form_schema?.name} - ${item?.id?.slice(0, 7)}`}</RoutingLink>
        ),
      },
      {
        label: "Form Template",
        key: "form_schema.id",
        isGroupable: true,
        renderComponent: ({ item }) => {
          return (
            <RoutingLink
              to={linkToFormTemplateDetail(
                item.form_schema?.form_category_id,
                item.form_schema_id
              )}
            >
              {item.form_schema?.name}
            </RoutingLink>
          );
        },
      },
      {
        label: "Form Category",
        key: "form_schema.form_category_id",
        renderComponent: ({ item }) => {
          return (
            <RoutingLink
              to={linkToFormCategoryDetails(item.form_schema?.form_category_id)}
            >
              {item.form_schema?.form_category?.name}
            </RoutingLink>
          );
        },
      },
      {
        label: "Saved At",
        key: "created_at",
        isSortable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell value={item.created_at} />
        ),
      },
      {
        label: "Saved By",
        key: "created_by",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) =>
          item.created_by ? (
            <UserDataDisplay
              id={item.created_by}
              name={
                formSubmissionsCreatedByUsersMap[item.created_by]?.name ?? "n/a"
              }
            />
          ) : (
            "-"
          ),
      },
    ],
    [formSubmissionsCreatedByUsersMap]
  );

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: formSubmissionsGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemId: (item) => item.id,
    totalSelectableItems,
  });

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <FormSubmissionsViewFilterArea
          viewConfigStorageKey={viewConfigStorageKey}
        />
      </div>
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          title: "Drafts",
        }}
        filterComponent={
          <FormSubmissionsTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            filterDrawerContentSlot={
              <FormSubmissionsTableFilterAreaDrawerContent
                hasStatusFilter={false}
                hasCategoryFilter
                hasTemplateFilter
              />
            }
          />
        }
        dataTableProps={{
          intent: "simplicity-first",
          items: draftSubmissions?.data ?? [],
          getItemId: (item) => item.id,
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: draftSubmissions?.total_entries,
            pageSizeText: "rows per page",
          },
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          headers,
          isLoading: isSubmissionsLoading,
          isBusy: isSubmissionsFetching,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          getItemActions: ({
            item,
          }: {
            item: FormSubmissionType;
          }): ItemAction[] => [
            {
              label: "Export as PDF",
              buttonProps: {
                icon: "share",
                variant: "text",
                isLoading: exportPDFMutation.isLoading,
                onClick: () => {
                  exportPDFMutation.mutate(item);
                },
              },
            },
          ],
        }}
        {...groupByDataTableProps}
        columnConfigurationProps={{
          onSaveColumnConfigurationAsDefault: (colsToSave) =>
            saveDefaultConfigurationMutation
              .mutateAsync({
                name: "Default for draft Form Submissions list table",
                configuration: {
                  columns: colsToSave,
                },
              })
              .then(() => true)
              .catch(() => false),
          isLoadingSaveDefaultColumnConfiguration:
            saveDefaultConfigurationMutation.isLoading,
          isSaveDefaultColumnConfigurationAvailable:
            isSaveConfigurationAvailable,
          onResetColumnConfiguration: () =>
            resetConfigurationMutation.mutateAsync(),
          onDeleteDefaultColumnConfiguration: () =>
            deleteDefaultConfigurationMutation
              .mutateAsync()
              .then(() => true)
              .catch(() => false),
          isLoadingDeleteDefaultColumnConfiguration:
            deleteDefaultConfigurationMutation.isLoading,
          isDeleteDefaultColumnConfigurationAvailable:
            isDeleteConfigurationAvailable && !!matchingCompanyConfiguration,
          isLoadingDefaultColumnConfiguration:
            isLoadingTableUserInterfaceConfiguration,
          isDefaultColumnConfigurationAvailable: !!matchingDefaultConfiguration,
          isCompanyColumnConfigurationAvailable: !!matchingCompanyConfiguration,
          defaultColumnConfiguration:
            matchingDefaultConfiguration?.configuration?.columns,
          companyDefaultColumnConfiguration:
            matchingCompanyConfiguration?.configuration?.columns,
          personalDefaultColumnConfiguration:
            matchingPersonalConfiguration?.configuration?.columns,
        }}
      />
    </>
  );
};
